import React from 'react';

// Custom event for proposing code changes from external sources (e.g., Copilot tools)
export const PROPOSE_CODE_EVENT = 'codeEditor:proposeCode';

export type ProposeCodeEventDetail = {
  nodeID: string;
  code: string;
};

declare global {
  interface WindowEventMap {
    [PROPOSE_CODE_EVENT]: CustomEvent<ProposeCodeEventDetail>;
  }
}

export type CodeEditorState = {
  /** The original code before any proposed changes */
  originalCode: string;
  /** The proposed code from AI (when in diff mode) */
  proposedCode: string | null;
  /** Whether diff mode is active */
  isDiffMode: boolean;
  /** The node ID this code belongs to */
  nodeID: string | null;
  /** Propose new code (enters diff mode) */
  proposeCode: (code: string) => void;
  /** Accept the proposed code */
  acceptProposal: () => void;
  /** Reject the proposed code */
  rejectProposal: () => void;
};

const CodeEditorContext = React.createContext<CodeEditorState | null>(null);

export function useCodeEditorContext() {
  const context = React.useContext(CodeEditorContext);
  if (!context) {
    throw new Error(
      'useCodeEditorContext must be used within a CodeEditorProvider'
    );
  }
  return context;
}

export function useCodeEditorContextOptional() {
  return React.useContext(CodeEditorContext);
}

type CodeEditorProviderProps = React.PropsWithChildren<{
  originalCode: string;
  nodeID: string | null;
  onAccept: (code: string) => void;
}>;

export function CodeEditorProvider({
  children,
  originalCode,
  nodeID,
  onAccept,
}: CodeEditorProviderProps) {
  const [proposedCode, setProposedCode] = React.useState<string | null>(null);

  const proposeCode = React.useCallback((code: string) => {
    setProposedCode(code);
  }, []);

  const acceptProposal = React.useCallback(() => {
    if (proposedCode !== null) {
      onAccept(proposedCode);
      setProposedCode(null);
    }
  }, [proposedCode, onAccept]);

  const rejectProposal = React.useCallback(() => {
    setProposedCode(null);
  }, []);

  // Listen for external code proposals (from Copilot tools)
  React.useEffect(() => {
    function handleProposeCode(event: CustomEvent<ProposeCodeEventDetail>) {
      // Only handle events for this specific node
      if (event.detail.nodeID === nodeID) {
        setProposedCode(event.detail.code);
      }
    }

    window.addEventListener(PROPOSE_CODE_EVENT, handleProposeCode);
    return () => {
      window.removeEventListener(PROPOSE_CODE_EVENT, handleProposeCode);
    };
  }, [nodeID]);

  const value: CodeEditorState = {
    originalCode,
    proposedCode,
    isDiffMode: proposedCode !== null,
    nodeID,
    proposeCode,
    acceptProposal,
    rejectProposal,
  };

  return (
    <CodeEditorContext.Provider value={value}>
      {children}
    </CodeEditorContext.Provider>
  );
}
